import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { X, Download, ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACCENT = "#FF1744";

const CATEGORIES = [
  "Residential Homes",
  "Multi-Unit Developments",
  "Childcare Centres",
  "Shop Fit-Outs",
  "Medical Facilities",
  "Knock-Down and Rebuild",
  "Other Commercial / Custom Build",
];

const LAND_SIZES = [
  "Under 300 m²",
  "300 m² – 500 m²",
  "500 m² – 800 m²",
  "800 m² – 1,500 m²",
  "1,500 m²+ / Large Acreage",
];

const CONFIGS = [
  "3 Bed, 2 Bath",
  "4 Bed, 2.5 Bath",
  "5+ Bed Luxury Estate",
  "Multi-Unit / Duplex Layout",
  "Commercial / Fit-Out Floorplan",
];

const BUDGETS = [
  "$350k – $600k",
  "$600k – $1.2M",
  "$1.2M – $2.5M",
  "$2.5M+ / Commercial Scope",
];

const inputCls =
  "w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-white placeholder:text-white/30 outline-none transition-colors duration-300 focus:border-[#FF1744] focus:ring-2 focus:ring-[#FF1744]/25";
const selectCls = `${inputCls} appearance-none cursor-pointer [&>option]:bg-[#0A0E17]`;

const STEP_TITLES = ["Your Details", "Project Type", "Specs & Location", "Your Vision"];

const Toggle = ({ options, value, onChange, testId }) => (
  <div className="grid grid-cols-2 gap-2">
    {options.map(([val, label]) => (
      <button
        type="button"
        key={val}
        data-testid={`${testId}-${val}`}
        onClick={() => onChange(val)}
        className={`rounded-full px-3 py-2.5 font-display text-xs font-semibold border transition-all duration-300 ${
          value === val
            ? "border-[#FF1744] bg-[#FF1744]/15 text-white shadow-[0_0_18px_rgba(255,23,68,0.3)]"
            : "border-white/10 text-white/50 hover:border-white/30"
        }`}
      >
        {label}
      </button>
    ))}
  </div>
);

const Label = ({ children }) => (
  <p className="font-display text-[11px] font-bold tracking-[0.2em] text-white/50 mb-2.5">{children}</p>
);

export const ProjectQueryWizard = ({ open, onOpenChange, presetCategory }) => {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    category: CATEGORIES[0],
    purpose: "occupy",
    height: "single",
    location: "",
    land_size: LAND_SIZES[1],
    configuration: CONFIGS[0],
    budget_range: BUDGETS[1],
    notes: "",
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setVal = (k) => (v) => setForm({ ...form, [k]: v });

  const reset = () => {
    setStep(0);
    setDone(false);
    setForm({
      name: "", email: "", phone: "", category: presetCategory || CATEGORIES[0], purpose: "occupy",
      height: "single", location: "", land_size: LAND_SIZES[1], configuration: CONFIGS[0],
      budget_range: BUDGETS[1], notes: "",
    });
  };

  useEffect(() => {
    if (open) reset();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOpenChange = (v) => {
    onOpenChange(v);
    if (!v) setTimeout(reset, 300);
  };

  const next = () => {
    if (step === 0) {
      if (!form.name.trim()) return toast.error("Please add your name.");
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) return toast.error("Please add a valid email.");
      if (!form.phone.trim() || form.phone.trim().length < 8) return toast.error("Please add a contact number.");
    }
    setStep((s) => Math.min(s + 1, 3));
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/leads/project`, form);
      if (!data.email_dispatched) {
        toast.warning("Query saved, but the email alert hiccuped — we'll still see it in our inbox.");
      }
      setDone(true);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const downloadGuide = async () => {
    try {
      const res = await axios.get(`${API}/company-guide`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "JAI-HOMES-Company-Guide.pdf";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Couldn't fetch the guide — try again.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
          className="bg-[#0A0E17]/95 backdrop-blur-2xl border border-[#FF1744]/30 text-white sm:max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-[0_0_80px_rgba(255,23,68,0.15),0_40px_120px_rgba(0,0,0,0.7)] [&>button]:hidden"
          data-testid="project-query-modal"
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="font-display text-[10px] font-bold tracking-[0.3em] text-[#FF1744] mb-1.5">
                JAI HOMES — DIRECT TO OUR ENGINEERING TEAM
              </p>
              <DialogTitle className="font-display text-2xl font-extrabold tracking-tight text-white">
                Project Query Wizard
              </DialogTitle>
              <DialogDescription className="text-xs text-white/50 mt-1.5">
                Four quick steps — your brief lands straight in the JAI HOMES inbox.
              </DialogDescription>
            </div>
            <button
              onClick={() => handleOpenChange(false)}
              data-testid="wizard-modal-close"
              aria-label="Close wizard"
              className="shrink-0 w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-white/50 hover:text-white hover:border-[#FF1744]/60 transition-colors duration-300"
            >
              <X size={16} />
            </button>
          </div>

          {!done && (
            <div className="mt-6" data-testid="wizard-modal-progress">
              <div className="flex items-center gap-2">
                {STEP_TITLES.map((t, i) => (
                  <span
                    key={t}
                    className="h-1.5 rounded-full transition-all duration-500"
                    style={{
                      flex: step === i ? "2 1 0%" : "1 1 0%",
                      background: i <= step ? ACCENT : "rgba(255,255,255,0.1)",
                      boxShadow: i === step ? `0 0 12px ${ACCENT}` : "none",
                    }}
                  />
                ))}
              </div>
              <p className="mt-3 font-display text-[11px] tracking-[0.2em] text-white/40">
                STEP {step + 1} OF 4 — {STEP_TITLES[step].toUpperCase()}
              </p>
            </div>
          )}

          <AnimatePresence mode="wait">
            <motion.div
              key={done ? "done" : step}
              initial={{ opacity: 0, x: 28 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -28 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="mt-6"
            >
              {!done && step === 0 && (
                <div className="space-y-4">
                  <div>
                    <Label>CLIENT NAME *</Label>
                    <input className={inputCls} placeholder="Your full name" value={form.name} onChange={set("name")} data-testid="pq-name" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label>EMAIL ADDRESS *</Label>
                      <input className={inputCls} type="email" placeholder="you@example.com" value={form.email} onChange={set("email")} data-testid="pq-email" />
                    </div>
                    <div>
                      <Label>PHONE NUMBER *</Label>
                      <input className={inputCls} type="tel" placeholder="04XX XXX XXX" value={form.phone} onChange={set("phone")} data-testid="pq-phone" />
                    </div>
                  </div>
                </div>
              )}

              {!done && step === 1 && (
                <div className="space-y-6">
                  <div>
                    <Label>PROJECT CATEGORY</Label>
                    <select className={selectCls} value={form.category} onChange={set("category")} data-testid="pq-category">
                      {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label>PURPOSE</Label>
                    <Toggle options={[["occupy", "Occupy / Own"], ["invest", "Investment / Lease"]]} value={form.purpose} onChange={setVal("purpose")} testId="pq-purpose" />
                  </div>
                  <div>
                    <Label>BUILDING HEIGHT</Label>
                    <Toggle options={[["single", "Single Storey"], ["multi", "Double / Multi-Storey"]]} value={form.height} onChange={setVal("height")} testId="pq-height" />
                  </div>
                </div>
              )}

              {!done && step === 2 && (
                <div className="space-y-5">
                  <div>
                    <Label>PROJECT LOCATION / SUBURB</Label>
                    <input className={inputCls} placeholder='e.g. "Penrith, NSW" or "Liverpool, NSW"' value={form.location} onChange={set("location")} data-testid="pq-location" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label>HOUSE / LAND SIZE</Label>
                      <select className={selectCls} value={form.land_size} onChange={set("land_size")} data-testid="pq-land-size">
                        {LAND_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label>CONFIGURATION</Label>
                      <select className={selectCls} value={form.configuration} onChange={set("configuration")} data-testid="pq-configuration">
                        {CONFIGS.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <Label>ESTIMATED BUDGET RANGE</Label>
                    <select className={selectCls} value={form.budget_range} onChange={set("budget_range")} data-testid="pq-budget">
                      {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </div>
                </div>
              )}

              {!done && step === 3 && (
                <div>
                  <Label>DETAILED QUERY / PROJECT VISION</Label>
                  <textarea
                    className={`${inputCls} min-h-[140px] resize-none`}
                    placeholder="Land details, timeline, must-haves, questions for our engineering team..."
                    value={form.notes}
                    onChange={set("notes")}
                    data-testid="pq-notes"
                  />
                  <p className="mt-3 text-[11px] text-white/40 leading-relaxed">
                    Submitting sends your brief directly to admin@jaihomes.com.au — NSW Builder
                    Licence 476816C.
                  </p>
                </div>
              )}

              {done && (
                <div className="text-center py-6" data-testid="pq-success">
                  <CheckCircle2 size={52} style={{ color: ACCENT }} className="mx-auto mb-5" />
                  <h3 className="font-display text-2xl font-extrabold text-white tracking-tight">
                    Project Query Received
                  </h3>
                  <p className="mt-3 text-sm text-white/60 max-w-md mx-auto leading-relaxed">
                    Thank you for submitting your site details. Our estimating team under NSW
                    Builder Licence 476816C will review your budget and zoning parameters. An
                    initial feasibility outline will be dispatched to your email within 24–48
                    business hours.
                  </p>
                  <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                    <button
                      onClick={() => handleOpenChange(false)}
                      data-testid="pq-close-success"
                      className="rounded-full px-8 py-3.5 font-display text-sm font-bold text-white bg-gradient-to-r from-[#FF3D00] to-[#FF1744] hover:shadow-[0_0_28px_rgba(255,23,68,0.5)] hover:scale-[1.03] transition-all duration-300"
                    >
                      Return to Site
                    </button>
                    <a
                      href="tel:0413328202"
                      data-testid="pq-call-admin"
                      className="inline-flex items-center gap-2 rounded-full px-8 py-3.5 font-display text-sm font-semibold text-white/80 border border-white/20 hover:border-[#00E676]/60 hover:text-white transition-colors duration-300"
                    >
                      Call Admin Directly
                    </a>
                  </div>
                  <button
                    onClick={downloadGuide}
                    data-testid="pq-download-guide"
                    className="mt-5 inline-flex items-center gap-2 font-display text-xs font-semibold text-white/50 hover:text-white transition-colors duration-300"
                  >
                    <Download size={14} /> Download Company Guide (PDF)
                  </button>
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          {!done && (
            <div className="mt-8 flex items-center justify-between">
              <button
                onClick={() => setStep((s) => Math.max(s - 1, 0))}
                disabled={step === 0}
                data-testid="pq-back-btn"
                className="inline-flex items-center gap-2 font-display text-sm font-semibold text-white/50 hover:text-white disabled:opacity-0 transition-colors duration-300"
              >
                <ArrowLeft size={16} /> Back
              </button>
              {step < 3 ? (
                <button
                  onClick={next}
                  data-testid="pq-next-btn"
                  className="inline-flex items-center gap-2 rounded-full px-7 py-3 font-display text-sm font-bold text-white bg-gradient-to-r from-[#FF3D00] to-[#FF1744] hover:shadow-[0_0_24px_rgba(255,23,68,0.45)] hover:scale-[1.03] transition-all duration-300"
                >
                  Continue <ArrowRight size={16} />
                </button>
              ) : (
                <button
                  onClick={submit}
                  disabled={submitting}
                  data-testid="pq-submit-btn"
                  className="inline-flex items-center gap-2 rounded-full px-7 py-3 font-display text-sm font-bold text-white bg-gradient-to-r from-[#FF3D00] to-[#FF1744] hover:shadow-[0_0_24px_rgba(255,23,68,0.45)] hover:scale-[1.03] transition-all duration-300 disabled:opacity-50"
                >
                  {submitting ? "Sending..." : "Submit Query"} <ArrowRight size={16} />
                </button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
  );
};
